import type { Operation } from '@apollo/client';
import { GRAPHQL_URL, tokenStorage } from './types';

const MAX_REFRESH_ATTEMPTS = 2;

type PendingRequestCallback = (newToken: string) => void;

interface RefreshState {
  isRefreshing: boolean;
  pendingRequests: PendingRequestCallback[];
  refreshAttempts: number;
}

const state: RefreshState = {
  isRefreshing: false,
  pendingRequests: [],
  refreshAttempts: 0,
};

export function resetRefreshState(): void {
  state.isRefreshing = false;
  state.pendingRequests = [];
  state.refreshAttempts = 0;
}

export function isCurrentlyRefreshing(): boolean {
  return state.isRefreshing;
}

export function hasExceededMaxAttempts(): boolean {
  return state.refreshAttempts >= MAX_REFRESH_ATTEMPTS;
}

export function queuePendingRequest(callback: PendingRequestCallback): void {
  state.pendingRequests.push(callback);
}

function resolvePendingRequests(newToken: string): void {
  state.pendingRequests.forEach(callback => callback(newToken));
  state.pendingRequests = [];
}

export function clearTokens(): void {
  tokenStorage.clear();
}

export function getRefreshToken(): string | null {
  return tokenStorage.getRefreshToken();
}

export async function performTokenRefresh(abortSignal?: AbortSignal): Promise<string | null> {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return null;
  }

  state.isRefreshing = true;
  state.refreshAttempts++;

  try {
    const response = await fetch(GRAPHQL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      signal: abortSignal,
      body: JSON.stringify({
        query: `
          mutation RefreshToken($input: RefreshTokenInput!) {
            refreshToken(input: $input) {
              accessToken
              expiresIn
            }
          }
        `,
        variables: { input: { refreshToken } },
      }),
    });

    const result = await response.json();

    if (result.data?.refreshToken?.accessToken) {
      const newAccessToken = result.data.refreshToken.accessToken;
      tokenStorage.setAccessToken(newAccessToken);

      state.refreshAttempts = 0;
      state.isRefreshing = false;
      resolvePendingRequests(newAccessToken);
      return newAccessToken;
    }

    state.isRefreshing = false;
    return null;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      state.isRefreshing = false;
      return null;
    }
    console.error('[TokenRefresh] Error:', error);
    state.isRefreshing = false;
    return null;
  }
}

export const RETRY_CONTEXT_KEY = 'hasRetriedAfterRefresh';

export function updateOperationWithNewToken(operation: Operation, newToken: string): void {
  const oldHeaders = operation.getContext().headers;
  operation.setContext({
    headers: {
      ...oldHeaders,
      authorization: `Bearer ${newToken}`,
    },
    [RETRY_CONTEXT_KEY]: true,
  });
}
